import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../../../../redux/hooks';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Box, useMediaQuery
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

interface DeleteConfirmationModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
}

const DeleteConfirmationModal: React.FC<DeleteConfirmationModalProps> = ({ open, onClose, onConfirm, title, message }) => {
  const { t } = useTranslation();
  const colors = useAppSelector((state) => state.theme.colors);
  const isMobile = useMediaQuery('(max-width: 600px)');

  const cancelButtonStyle = {
    backgroundColor: colors?.interface,
    color: colors?.text,
    border: `1px solid ${colors?.folderDataRowBorderColor}`,
    '&:hover': { 
      backgroundColor: colors?.sectionHover,
      borderColor: colors?.lightBlue
    },
    fontWeight: 'bold',
    borderRadius: 2,
    width: isMobile ? '100%' : 'auto',
  };

  const deleteButtonStyle = {
    backgroundColor: colors?.red,
    color: '#fff',
    '&:hover': {
      backgroundColor: '#d32f2f', // A darker red
    },
    fontWeight: 'bold',
    borderRadius: 2,
    width: isMobile ? '100%' : 'auto',
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          backgroundColor: colors?.container,
          borderRadius: 4,
          border: `1px solid ${colors?.folderDataRowBorderColor}`
        }
      }}
    >
      <DialogTitle sx={{ color: colors?.text, fontWeight: 'bold', borderBottom: `1px solid ${colors?.folderDataRowBorderColor}` }}>
        {title}
      </DialogTitle>
      <DialogContent sx={{ pt: '20px !important', backgroundColor: colors?.container }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
          <WarningAmberIcon sx={{ color: colors?.red, fontSize: 40 }} />
          <Typography sx={{ color: colors?.text }}>
            {message}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ 
        p: '16px 24px', 
        borderTop: `1px solid ${colors?.folderDataRowBorderColor}`,
        flexDirection: isMobile ? 'column-reverse' : 'row',
        gap: isMobile ? 1.5 : 1,
      }}>
        <Button onClick={onClose} sx={cancelButtonStyle}>
          {t('cancel')}
        </Button>
        <Button onClick={onConfirm} sx={deleteButtonStyle}>
          {t('delete')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteConfirmationModal;